import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { protocolsAPI, catalogAPI } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, 
  Pencil, 
  Trash, 
  ListChecks, 
  Pill,
  FirstAid,
  Shield,
  Copy,
  Check,
  Spinner,
  MagnifyingGlass,
  Warning
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const CLINICAL_ROLES = [
  { id: 'medico', name: 'Médico', color: 'bg-blue-500' },
  { id: 'enfermera', name: 'Enfermera', color: 'bg-pink-500' },
  { id: 'tens', name: 'TENS', color: 'bg-green-500' },
  { id: 'paramedico', name: 'Paramédico', color: 'bg-orange-500' }
];

const PROTOCOL_TYPES = [
  { id: 'minsal', name: 'MINSAL', description: 'Ministerio de Salud de Chile' },
  { id: 'hospital', name: 'Hospital', description: 'Protocolo institucional' },
  { id: 'universidad', name: 'Universidad', description: 'Protocolo universitario' },
  { id: 'institucional', name: 'Institucional', description: 'Protocolo institucional personalizado' }
];

export default function ProtocolsAdminPage() {
  const { user } = useAuth();
  const [protocols, setProtocols] = useState([]);
  const [medications, setMedications] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    nombre: '',
    tipo: 'minsal',
    descripcion: '',
    medicamentos: [],
    acciones: [],
    is_active: true
  });

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [protocolsRes, medsRes, actionsRes] = await Promise.all([
          protocolsAPI.getAll(),
          catalogAPI.getMedications(),
          catalogAPI.getClinicalActions()
        ]);
        setProtocols(protocolsRes.data);
        setMedications(medsRes.data);
        setActions(actionsRes.data);
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Error al cargar los datos');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Filter protocols by search term
  const filteredProtocols = protocols.filter(p => 
    p.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.tipo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle create
  const handleCreate = async () => {
    try {
      setSaving(true);
      const response = await protocolsAPI.create(formData);
      setProtocols(prev => [...prev, response.data]);
      setShowCreateModal(false);
      resetForm();
      toast.success('Protocolo creado exitosamente');
    } catch (error) {
      console.error('Error creating protocol:', error);
      toast.error('Error al crear el protocolo');
    } finally {
      setSaving(false);
    }
  };

  // Handle update
  const handleUpdate = async () => {
    if (!selectedProtocol) return;
    try {
      setSaving(true);
      const response = await protocolsAPI.update(selectedProtocol.id, formData);
      setProtocols(prev => prev.map(p => p.id === selectedProtocol.id ? response.data : p));
      setShowEditModal(false);
      setSelectedProtocol(null);
      resetForm();
      toast.success('Protocolo actualizado exitosamente');
    } catch (error) {
      console.error('Error updating protocol:', error);
      toast.error('Error al actualizar el protocolo');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedProtocol) return;
    try {
      setSaving(true);
      await protocolsAPI.delete(selectedProtocol.id);
      setProtocols(prev => prev.filter(p => p.id !== selectedProtocol.id));
      setShowDeleteModal(false);
      setSelectedProtocol(null);
      toast.success('Protocolo eliminado exitosamente');
    } catch (error) {
      console.error('Error deleting protocol:', error);
      toast.error('Error al eliminar el protocolo');
    } finally {
      setSaving(false);
    }
  };

  // Handle duplicate
  const handleDuplicate = async (protocol) => {
    try {
      const duplicateData = {
        ...protocol,
        nombre: `${protocol.nombre} (Copia)`,
        id: undefined
      };
      const response = await protocolsAPI.create(duplicateData);
      setProtocols(prev => [...prev, response.data]);
      toast.success('Protocolo duplicado exitosamente');
    } catch (error) {
      console.error('Error duplicating protocol:', error);
      toast.error('Error al duplicar el protocolo');
    }
  };

  // Open edit modal
  const openEditModal = (protocol) => {
    setSelectedProtocol(protocol);
    setFormData({
      nombre: protocol.nombre || '',
      tipo: protocol.tipo || 'minsal',
      descripcion: protocol.descripcion || '',
      medicamentos: protocol.medicamentos || [],
      acciones: protocol.acciones || [],
      is_active: protocol.is_active !== false
    });
    setShowEditModal(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      nombre: '',
      tipo: 'minsal',
      descripcion: '',
      medicamentos: [],
      acciones: [],
      is_active: true
    });
  };

  // Toggle medication in protocol
  const toggleMedication = (med) => {
    setFormData(prev => {
      const exists = prev.medicamentos.find(m => m.medication_id === med.id);
      if (exists) {
        return {
          ...prev,
          medicamentos: prev.medicamentos.filter(m => m.medication_id !== med.id)
        };
      } else {
        return {
          ...prev,
          medicamentos: [...prev.medicamentos, {
            medication_id: med.id,
            nombre: med.nombre,
            dosis_sugerida: med.dosis_sugerida || med.dosis || '1 unidad',
            roles_autorizados: ['medico', 'enfermera'],
            vias_permitidas: med.vias || ['IV', 'IM']
          }]
        };
      }
    });
  };

  // Toggle action in protocol
  const toggleAction = (action) => {
    setFormData(prev => {
      const exists = prev.acciones.find(a => a.action_id === action.id);
      if (exists) {
        return {
          ...prev,
          acciones: prev.acciones.filter(a => a.action_id !== action.id)
        };
      } else {
        return {
          ...prev,
          acciones: [...prev.acciones, {
            action_id: action.id,
            nombre: action.nombre,
            roles_autorizados: ['medico', 'enfermera', 'tens']
          }]
        };
      }
    });
  };

  // Toggle role for medication
  const toggleMedicationRole = (medId, roleId) => {
    setFormData(prev => ({
      ...prev,
      medicamentos: prev.medicamentos.map(m => {
        if (m.medication_id === medId) {
          const roles = m.roles_autorizados || [];
          if (roles.includes(roleId)) {
            return { ...m, roles_autorizados: roles.filter(r => r !== roleId) };
          } else {
            return { ...m, roles_autorizados: [...roles, roleId] };
          }
        }
        return m;
      })
    }));
  };

  // Toggle role for action
  const toggleActionRole = (actionId, roleId) => {
    setFormData(prev => ({
      ...prev,
      acciones: prev.acciones.map(a => {
        if (a.action_id === actionId) {
          const roles = a.roles_autorizados || [];
          if (roles.includes(roleId)) {
            return { ...a, roles_autorizados: roles.filter(r => r !== roleId) };
          } else {
            return { ...a, roles_autorizados: [...roles, roleId] };
          }
        }
        return a;
      })
    }));
  };

  // Check if user is admin
  const isAdmin = user?.role === 'super_admin' || user?.role === 'superadmin' || user?.role === 'admin';

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center py-12">
          <Spinner size={48} className="animate-spin text-blue-500" />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ListChecks size={28} className="text-blue-600" />
              Protocolos Clínicos
            </h1>
            <p className="text-slate-500 mt-1">
              Gestiona los protocolos, medicamentos y acciones disponibles para cada rol clínico
            </p>
          </div>
          {isAdmin && (
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700 gap-2"
              data-testid="create-protocol-btn"
            >
              <Plus size={20} />
              Nuevo Protocolo
            </Button>
          )}
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Buscar protocolos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-protocols"
              />
            </div>
          </CardContent>
        </Card>

        {/* Protocols Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProtocols.map((protocol) => (
            <Card key={protocol.id} className="bg-white border-slate-200 hover:shadow-md transition-shadow" data-testid={`protocol-card-${protocol.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-slate-900 text-lg">{protocol.nombre}</CardTitle>
                    <CardDescription className="text-slate-500 mt-1">
                      <Badge variant="outline" className="mr-2 bg-blue-50 text-blue-700 border-blue-200">{protocol.tipo}</Badge>
                      {!protocol.is_active && <Badge variant="destructive">Inactivo</Badge>}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-slate-500 mb-4 line-clamp-2">
                  {protocol.descripcion || 'Sin descripción'}
                </p>
                
                {/* Stats */}
                <div className="flex gap-4 mb-4">
                  <div className="flex items-center gap-1.5 text-sm text-slate-600">
                    <Pill size={16} className="text-green-500" />
                    <span>{protocol.medicamentos?.length || 0} medicamentos</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-600">
                    <FirstAid size={16} className="text-blue-500" />
                    <span>{protocol.acciones?.length || 0} acciones</span>
                  </div>
                </div>

                {/* Authorized Roles */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {CLINICAL_ROLES.map(role => {
                    const hasMeds = protocol.medicamentos?.some(m => m.roles_autorizados?.includes(role.id));
                    const hasActions = protocol.acciones?.some(a => a.roles_autorizados?.includes(role.id));
                    if (!hasMeds && !hasActions) return null;
                    return (
                      <Badge key={role.id} className={`${role.color} text-white text-xs`}>
                        {role.name}
                      </Badge>
                    );
                  })}
                </div>

                {/* Actions */}
                {isAdmin && (
                  <div className="flex gap-2 pt-3 border-t border-slate-200">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => openEditModal(protocol)}
                      className="flex-1 gap-1"
                      data-testid={`edit-protocol-${protocol.id}`}
                    >
                      <Pencil size={14} />
                      Editar
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleDuplicate(protocol)}
                      className="gap-1"
                      data-testid={`duplicate-protocol-${protocol.id}`}
                    >
                      <Copy size={14} />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        setSelectedProtocol(protocol);
                        setShowDeleteModal(true);
                      }}
                      className="gap-1 border-red-200 text-red-600 hover:bg-red-50"
                      data-testid={`delete-protocol-${protocol.id}`}
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          {filteredProtocols.length === 0 && (
            <div className="col-span-full text-center py-12">
              <ListChecks size={48} className="mx-auto text-slate-400 mb-4" />
              <p className="text-slate-500">No se encontraron protocolos</p>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => {
        if (!open) {
          setShowCreateModal(false);
          setShowEditModal(false);
          setSelectedProtocol(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListChecks size={24} className="text-blue-600" />
              {showCreateModal ? 'Crear Nuevo Protocolo' : 'Editar Protocolo'}
            </DialogTitle>
            <DialogDescription>
              Configure los medicamentos y acciones permitidas para cada rol clínico
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="general" className="mt-4">
            <TabsList>
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="medications">Medicamentos</TabsTrigger>
              <TabsTrigger value="actions">Acciones</TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nombre del Protocolo *</Label>
                  <Input
                    value={formData.nombre}
                    onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                    placeholder="Ej: Paro Cardiorrespiratorio Adulto"
                    className="mt-1"
                    data-testid="protocol-name-input"
                  />
                </div>
                <div>
                  <Label>Tipo de Protocolo *</Label>
                  <Select
                    value={formData.tipo}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, tipo: value }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROTOCOL_TYPES.map(type => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name} - {type.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Descripción</Label>
                <Textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                  placeholder="Descripción del protocolo..."
                  className="mt-1 min-h-[100px]"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
                <Label htmlFor="is_active">Protocolo activo</Label>
              </div>
            </TabsContent>

            <TabsContent value="medications" className="mt-4">
              <div className="text-sm text-slate-500 mb-3">
                Seleccione los medicamentos habilitados y defina qué roles pueden administrarlos
              </div>
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-3">
                  {medications.map(med => {
                    const inProtocol = formData.medicamentos.find(m => m.medication_id === med.id);
                    return (
                      <div 
                        key={med.id} 
                        className={`p-3 rounded-lg border ${inProtocol ? 'bg-green-50 border-green-300' : 'bg-slate-50 border-slate-200'}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={!!inProtocol}
                              onCheckedChange={() => toggleMedication(med)}
                              data-testid={`med-checkbox-${med.id}`}
                            />
                            <span className="font-medium text-slate-900">{med.nombre}</span>
                          </div>
                          {inProtocol && (
                            <Badge className="bg-green-600 text-white">
                              <Check size={12} className="mr-1" />
                              Incluido
                            </Badge>
                          )}
                        </div>
                        {inProtocol && (
                          <div className="ml-6 flex gap-2 flex-wrap">
                            {CLINICAL_ROLES.map(role => (
                              <Badge
                                key={role.id}
                                variant="outline"
                                className={`cursor-pointer transition-all ${
                                  inProtocol.roles_autorizados?.includes(role.id)
                                    ? `${role.color} text-white border-transparent`
                                    : 'border-slate-300 text-slate-500 hover:border-slate-400'
                                }`}
                                onClick={() => toggleMedicationRole(med.id, role.id)}
                              >
                                {role.name}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="actions" className="mt-4">
              <div className="text-sm text-slate-500 mb-3">
                Seleccione las acciones clínicas habilitadas y defina qué roles pueden realizarlas
              </div>
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-3">
                  {actions.map(action => {
                    const inProtocol = formData.acciones.find(a => a.action_id === action.id);
                    return (
                      <div 
                        key={action.id} 
                        className={`p-3 rounded-lg border ${inProtocol ? 'bg-blue-50 border-blue-300' : 'bg-slate-50 border-slate-200'}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={!!inProtocol}
                              onCheckedChange={() => toggleAction(action)}
                              data-testid={`action-checkbox-${action.id}`}
                            />
                            <span className="font-medium text-slate-900">{action.nombre}</span>
                          </div>
                          {inProtocol && (
                            <Badge className="bg-blue-600 text-white">
                              <Check size={12} className="mr-1" />
                              Incluida
                            </Badge>
                          )}
                        </div>
                        {inProtocol && (
                          <div className="ml-6 flex gap-2 flex-wrap">
                            {CLINICAL_ROLES.map(role => (
                              <Badge
                                key={role.id}
                                variant="outline"
                                className={`cursor-pointer transition-all ${
                                  inProtocol.roles_autorizados?.includes(role.id)
                                    ? `${role.color} text-white border-transparent`
                                    : 'border-slate-300 text-slate-500 hover:border-slate-400'
                                }`}
                                onClick={() => toggleActionRole(action.id, role.id)}
                              >
                                {role.name}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCreateModal(false);
                setShowEditModal(false);
                setSelectedProtocol(null);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={showCreateModal ? handleCreate : handleUpdate}
              disabled={!formData.nombre || saving}
              className="bg-blue-600 hover:bg-blue-700 gap-2"
              data-testid="save-protocol-btn"
            >
              {saving ? <Spinner size={16} className="animate-spin" /> : <Check size={16} />}
              {showCreateModal ? 'Crear Protocolo' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Warning size={24} />
              Eliminar Protocolo
            </DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas eliminar el protocolo "{selectedProtocol?.nombre}"? 
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteModal(false)}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleDelete}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700 gap-2"
              data-testid="confirm-delete-btn"
            >
              {saving ? <Spinner size={16} className="animate-spin" /> : <Trash size={16} />}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
